/**
 * Drone — 4-voice detuned FatOscillator with LFO-swept filter.
 *
 * Built for continuous texture beds: four detuned voices summed
 * through a 2-pole filter with its cutoff modulated by an LFO.
 * Dial in the spread + cutoff + LFO rate/depth to get anything
 * from gentle evolving pads to unstable noise beds.
 */

import {
  FatOscillator,
  Filter,
  LFO,
  AmplitudeEnvelope,
  Gain as ToneGain,
} from 'tone'
import { registerInstrument } from '@/audio/plugins/registry'
import type {
  InstrumentPlugin,
  InstrumentVoiceHandle,
} from '@/audio/plugins/types'
import { registerToneContext } from '@/audio/tone/registerContext'

let registered = false

export function registerDroneInstrument(): void {
  if (registered) return
  registered = true
  registerInstrument(droneInstrument)
}

const droneInstrument: InstrumentPlugin = {
  id: 'drone',
  label: 'Drone',
  category: 'synth',
  defaultPreset: {
    pitch: 48,
    spread: 25,
    cutoff: 1200,
    lfoRate: 0.2,
    lfoDepth: 800,
    attack: 1.5,
    release: 3,
  },
  paramSchema: [
    {
      id: 'pitch',
      label: 'Key',
      kind: 'slider',
      min: 24,
      max: 84,
      step: 1,
      default: 48,
      format: 'midi-note',
      help: 'Base note the fat-oscillator stack plays at. Per-step pitch locks add a semitone offset.',
    },
    {
      id: 'spread',
      label: 'Detune spread',
      kind: 'slider',
      min: 0,
      max: 100,
      step: 1,
      default: 25,
    },
    {
      id: 'cutoff',
      label: 'Cutoff',
      kind: 'freq',
      min: 100,
      max: 12000,
      step: 10,
      default: 1200,
    },
    {
      id: 'lfoRate',
      label: 'LFO Rate',
      kind: 'slider',
      min: 0.01,
      max: 10,
      step: 0.01,
      default: 0.2,
    },
    {
      id: 'lfoDepth',
      label: 'LFO Depth',
      kind: 'freq',
      min: 0,
      max: 6000,
      step: 10,
      default: 800,
    },
    {
      id: 'attack',
      label: 'Attack',
      kind: 'time',
      min: 0.01,
      max: 10,
      step: 0.01,
      default: 1.5,
    },
    {
      id: 'release',
      label: 'Release',
      kind: 'time',
      min: 0.1,
      max: 10,
      step: 0.1,
      default: 3,
    },
  ],
  createVoice: (ctx, preset = {}) => createDroneVoice(ctx, preset),
}

function createDroneVoice(
  ctx: AudioContext,
  preset: Record<string, unknown>,
): InstrumentVoiceHandle {
  registerToneContext(ctx)

  const p = preset as Record<string, number>
  const basePitch = typeof p.pitch === 'number' ? p.pitch : 48
  const spread = typeof p.spread === 'number' ? p.spread : 25
  const cutoff = typeof p.cutoff === 'number' ? p.cutoff : 1200
  const lfoRate = typeof p.lfoRate === 'number' ? p.lfoRate : 0.2
  const lfoDepth = typeof p.lfoDepth === 'number' ? p.lfoDepth : 800
  const attack = typeof p.attack === 'number' ? p.attack : 1.5
  const release = typeof p.release === 'number' ? p.release : 3

  const fat = new FatOscillator({
    frequency: 0,
    type: 'sawtooth',
    count: 4,
    spread,
  })
  const filter = new Filter({
    type: 'lowpass',
    frequency: cutoff,
    Q: 1,
    rolloff: -24,
  })
  const lfo = new LFO({
    frequency: lfoRate,
    min: Math.max(40, cutoff - lfoDepth),
    max: cutoff + lfoDepth,
  })
  const ampEnv = new AmplitudeEnvelope({
    attack,
    decay: 0.1,
    sustain: 1,
    release,
  })
  const output = new ToneGain({ gain: 1 })

  fat.connect(filter).connect(ampEnv).connect(output)
  lfo.connect(filter.frequency)

  let disposed = false
  let releasedAt: number | null = null

  return {
    output: output.output as unknown as AudioNode,
    trigger(when, velocity, pitchSemitones) {
      if (disposed) return
      const midi = basePitch + pitchSemitones
      const freq = 440 * Math.pow(2, (midi - 69) / 12)
      fat.frequency.value = freq
      const startT = Math.max(when, ctx.currentTime)
      fat.start(startT)
      lfo.start(startT)
      ampEnv.triggerAttack(startT, Math.max(0, Math.min(1, velocity)))
    },
    release(when) {
      if (disposed || releasedAt !== null) return
      const releaseT = Math.max(when, ctx.currentTime)
      releasedAt = releaseT
      ampEnv.triggerRelease(releaseT)
      const stopT = releaseT + release + 0.1
      try {
        fat.stop(stopT)
      } catch {
        /* already stopped */
      }
      try {
        lfo.stop(stopT)
      } catch {
        /* already stopped */
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      try {
        fat.dispose()
      } catch {
        /* already */
      }
      try {
        filter.dispose()
      } catch {
        /* already */
      }
      try {
        lfo.dispose()
      } catch {
        /* already */
      }
      try {
        ampEnv.dispose()
      } catch {
        /* already */
      }
      try {
        output.dispose()
      } catch {
        /* already */
      }
    },
  }
}
